// AuthFlow.tsx
import React, { useEffect } from "react";
import { Grid, Box, Avatar, Fade, Alert } from "@mui/material";
import SignInForm from "./SignInForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import FirstTimeSignInForm from "./FirstTimeSignInForm";
import MfaForm from "./MfaForm";
import bssiLogo from "../../assets/images/bssi_logo_light.png";
import { useAuth } from "../../context/AuthContext";
import ForgotPasswordSubmitForm from "./ForgotPasswordSubmitForm";
import MfaSetupForm from "./MfaSetupForm";

export default function AuthFlow() {
    const {
        flow,
        setFlow,
        error,
        resetError,
        forgotPassword,
        forgotPasswordSubmit,
        completeNewPassword,
        submitMfa,
        setupMfa,
        totp,
        mfaEmail,
        isLoading
    } = useAuth();

    // useEffect(() => {
    //     resetError();
    // }, [resetError]);

    const [resetEmail, setResetEmail] = React.useState<string>("");

    const handleForgotSubmit = async (data: { email: string }) => {
        await forgotPassword(data.email);
        setResetEmail(data.email); // Store email for the code verification step
    };

    const handleForgotPasswordSubmit = async (data: { code: string; newPassword: string }) => {
        await forgotPasswordSubmit(resetEmail, data.code, data.newPassword);
    };

    return (
        <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
                minHeight: "100vh",
                bgcolor: "background.default",
                p: 2
            }}
        >
            <Fade in={true} timeout={1000}>
                <Box
                    sx={{
                        maxWidth: "480px",
                        width: "100%",
                        p: 4,
                        bgcolor: "background.paper"
                        // borderRadius: 2,
                        // boxShadow: (theme) => theme.shadows[2]
                    }}
                >
                    {/* Logo */}
                    <Box sx={{ textAlign: "center" }}>
                        <Avatar
                            variant="rounded"
                            src={bssiLogo}
                            alt="Logo"
                            sx={{
                                width: 120,
                                height: 60,
                                mx: "auto",
                                mb: 2,
                                transition: "transform 0.3s",
                                "&:hover": { transform: "scale(1.05)" }
                            }}
                        />
                    </Box>

                    {/* Error Alert - positioned between logo and forms */}
                    {error && (
                        <Fade in={true}>
                            <Alert severity="error" onClose={resetError} sx={{ mb: 3, mt: 2 }}>
                                {error.message}
                            </Alert>
                        </Fade>
                    )}

                    {/* Auth Forms */}
                    {flow === "SIGNIN" && <SignInForm onForgotPassword={() => setFlow("FORGOT_PASSWORD")} />}

                    {flow === "FORGOT_PASSWORD" && (
                        <ForgotPasswordForm onSubmit={handleForgotSubmit} onBack={() => setFlow("SIGNIN")} />
                    )}

                    {flow === "FORGOT_PASSWORD_SUBMIT" && (
                        <ForgotPasswordSubmitForm
                            email={resetEmail}
                            onSubmit={handleForgotPasswordSubmit}
                            onBack={() => setFlow("SIGNIN")}
                        />
                    )}

                    {flow === "FIRST_TIME" && (
                        <FirstTimeSignInForm
                            onSubmit={({ email, tempPassword, newPassword }) =>
                                completeNewPassword(email, tempPassword, newPassword)
                            }
                            onBack={() => setFlow("SIGNIN")}
                        />
                    )}

                    {flow === "MFA" && (
                        <MfaForm onSubmit={({ mfaCode }) => submitMfa(mfaCode)} onBack={() => setFlow("SIGNIN")} />
                    )}

                    {flow === "MFA_SETUP" && (
                        <MfaSetupForm
                            totp={totp}
                            email={mfaEmail}
                            onSubmit={({ code }) => setupMfa(code)}
                            onBack={() => setFlow("SIGNIN")}
                            isLoading={isLoading}
                        />
                    )}
                </Box>
            </Fade>
        </Grid>
    );
}
